import { inject, Injectable, signal } from '@angular/core';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';
export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'top-center'
  | 'bottom-center';
export interface Toast {
  id: number;
  message: string;
  description?: string;
  severity: ToastSeverity;
  duration: number;
  position: ToastPosition;
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  toasts = signal<Toast[]>([]);
  position = signal<ToastPosition>('top-right');
  private idCounter = 0;

  /**
   * Muestra un toast (soporta tanto `show('Mensaje', 'success')` como `show('Éxito', 'Descripción', 'success')`)
   */
  show(
    messageOrTitle: string,
    descriptionOrSeverity?: string,
    severityOrDuration?: ToastSeverity | 'warn' | number,
    duration: number = 3000,
    position?: ToastPosition | undefined,
  ) {
    let finalTitle = messageOrTitle;
    let finalDesc: string | undefined = descriptionOrSeverity;
    let finalSeverity: ToastSeverity = 'info';
    let finalDuration = duration;

    const validSeverities: string[] = ['success', 'error', 'warning', 'warn', 'info'];

    if (descriptionOrSeverity && validSeverities.includes(descriptionOrSeverity.toLowerCase())) {
      // Invocación: show('Texto del mensaje', 'success')
      const sev = descriptionOrSeverity.toLowerCase() === 'warn' ? 'warning' : (descriptionOrSeverity.toLowerCase() as ToastSeverity);
      finalSeverity = sev;
      finalDesc = messageOrTitle;
      finalTitle = sev === 'success' ? 'Éxito' : sev === 'error' ? 'Error' : sev === 'warning' ? 'Advertencia' : 'Información';
      if (typeof severityOrDuration === 'number') {
        finalDuration = severityOrDuration;
      }
    } else {
      // Invocación: show('Título', 'Descripción', 'success', 3000) o show('Mensaje simple')
      if (typeof severityOrDuration === 'string' && validSeverities.includes(severityOrDuration.toLowerCase())) {
        finalSeverity = severityOrDuration.toLowerCase() === 'warn' ? 'warning' : (severityOrDuration.toLowerCase() as ToastSeverity);
      }
    }

    if (Array.isArray(finalDesc)) {
      finalDesc = (finalDesc as string[]).join(', ');
    } else if (finalDesc !== undefined && typeof finalDesc !== 'string') {
      finalDesc = String(finalDesc);
    }

    if (Array.isArray(finalTitle)) {
      finalTitle = (finalTitle as string[]).join(', ');
    } else if (typeof finalTitle !== 'string') {
      finalTitle = String(finalTitle);
    }

    // ── Deduplicación Preventiva Global (Evita doble toast en cualquier módulo) ──
    const isDuplicate = this.toasts().some(
      (t) =>
        ((t.description && finalDesc && t.description.trim() === finalDesc.trim()) ||
          (t.message && finalTitle && t.message.trim() === finalTitle.trim() && (!t.description || !finalDesc))) &&
        t.severity === finalSeverity
    );

    if (isDuplicate) {
      return;
    }

    const toast: Toast = {
      id: ++this.idCounter,
      message: finalTitle,
      description: finalDesc,
      severity: finalSeverity,
      duration: finalDuration,
      position: position || this.position(),
    };

    this.toasts.update((toasts) => [...toasts, toast]);

    if (finalDuration > 0) {
      setTimeout(() => this.remove(toast.id), finalDuration);
    }
  }

  success(message: string, description?: string, duration: number = 3000) {
    this.show(description ? message : 'Éxito', description || message, 'success', duration);
  }

  error(message: string, description?: string, duration: number = 4000) {
    this.show(description ? message : 'Error', description || message, 'error', duration);
  }

  warning(message: string, description?: string, duration: number = 3500) {
    this.show(description ? message : 'Advertencia', description || message, 'warning', duration);
  }

  info(message: string, description?: string, duration: number = 3000) {
    this.show(description ? message : 'Información', description || message, 'info', duration);
  }

  /**
   * Elimina un toast
   * @param id
   */
  remove(id: number) {
    this.toasts.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  /**
   *
   * @param position
   */
  setPosition(position: ToastPosition) {
    this.position.set(position);
  }
}
